// @flow
import queryString from 'query-string'
import {request, digestRequest, getDeviceIdentifier} from './base'
import type {
  MultipleRecordWithNumberOfPages,
  SingleRecord
} from 'modules/Common/types'
import type {
  AMNewDetail,
  AMDetail,
  ClientDetail,
  ClientNewDetail
} from 'modules/Admin/types/index'
import type {CompanyDetail} from 'modules/Admin/types'
import type {LoginParams} from 'modules/Authenticate/types/index'

export const getListAccountManagers = (
  pageNumber: number,
  pageSize: number,
  userIds?: Array<string>,
  companyId?: string,
  query?: string
): Promise<MultipleRecordWithNumberOfPages<AMDetail>> => {
  let params: Object = {
    pageNumber: userIds ? 1 : pageNumber,
    pageSize: userIds ? 1000 : pageSize,
    userIds,
    companyId,
    role: 'EXPERT'
  }
  if (query) {
    params.query = query
  }

  const urlQuery = queryString.stringify(params)
  return request(`user_management/users?${urlQuery}`, {
    method: 'GET'
  })
}

export const getListCompanies = (
  pageNumber: number,
  pageSize: number,
  companyIds?: Array<string>,
  query?: string
): Promise<MultipleRecordWithNumberOfPages<CompanyDetail>> => {
  let params: Object = {
    pageNumber: companyIds ? 1 : pageNumber,
    pageSize: companyIds ? 1000 : pageSize
  }
  if (companyIds) {
    params.companyIds = companyIds
  }
  if (query) {
    params.query = query
  }

  const urlQuery = queryString.stringify(params)
  return request(`user_management/companies?${urlQuery}`, {
    method: 'GET'
  })
}

export const inviteOrUpdateNewAM = (
  detail: AMNewDetail
): Promise<SingleRecord<AMDetail>> => {
  const body = {
    ...detail,
    user: {
      ...detail.user,
      roles: ['EXPERT']
    }
  }

  return request('user_management/users', {
    method: 'POST',
    body: JSON.stringify(body)
  })
}

export const nativeLogin = (params: LoginParams): Promise<any> => {
  const body = {
    username: params.username,
    company: params.company || params.realm,
    password: params.password,
    deviceIdentifier: getDeviceIdentifier()
  }
  return request(
    'authentication/password/admin-login',
    {
      method: 'POST',
      body: JSON.stringify(body)
    },
    true
  )
}

export const digestLogin = (
  params: LoginParams,
  isFirstTime: boolean
): Promise<any> => {
  return digestRequest(
    'authentication/admin/login',
    {
      method: 'POST',
      body: '{}'
    },
    params,
    isFirstTime
  )
}

export const addOrUpdateNewCompany = (detail: {
  company: CompanyDetail
}): Promise<SingleRecord<CompanyDetail>> => {
  const body = {
    ...detail,
    company: {
      ...detail.company,
      status: 'NEW'
    }
  }

  return request('user_management/companies', {
    method: 'POST',
    body: JSON.stringify(body)
  })
}

export const getSignedUrl = (body: string): Promise<any> => {
  return request('utils/uploads/pre_signed_url', {
    method: 'POST',
    body
  })
}

export const getClients = (
  pageNumber: number,
  pageSize: number
): Promise<MultipleRecordWithNumberOfPages<ClientDetail>> => {
  const params: Object = {
    pageNumber: pageNumber,
    pageSize: pageSize
  }

  const urlQuery = queryString.stringify(params)
  return request(`user_management/clients?${urlQuery}`, {
    method: 'GET'
  })
}

export const inviteNewClient = (
  detail: ClientNewDetail
): Promise<SingleRecord<ClientDetail>> => {
  const body = {
    ...detail
  }

  return request('user_management/clients', {
    method: 'POST',
    body: JSON.stringify(body)
  })
}

export const getCompanyProfile = (
  companyId: string
): Promise<SingleRecord<ClientDetail>> => {
  const params: Object = {
    companyId
  }

  const urlQuery = queryString.stringify(params)
  return request(`service_profile/company_profiles?${urlQuery}`, {
    method: 'GET'
  })
}

export const updateCompanyDefaultValue = (companyId: string): Promise<any> => {
  const body = {
    profile: {
      companyId,
      attributes: [
        {
          name: 'com.leapxpert.integration.wechat.miniapp',
          type: 'BOOLEAN',
          value: 'false'
        },
        {
          name: 'com.leapxpert.integration.whatsapp.native',
          type: 'BOOLEAN',
          value: 'false'
        },
        // TODO: Leap removal
        {
          name: 'com.leapxpert.integration.leap',
          type: 'BOOLEAN',
          value: 'false'
        }
      ]
    }
  }
  return request('service_profile/company_profiles', {
    method: 'PUT',
    body: JSON.stringify(body)
  })
}

export const getRolesList = (
  roleType: string,
): Promise<any> => {
  const params = {
    roleType
  }

  const urlQuery = queryString.stringify(params)
  return request(`roles?${urlQuery}`, {
    method: 'GET'
  })
}